import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import i18n from '@/utils/i18n';

import { PaymentDetails } from './PaymentDetails';
import {
  PageContainer,
  PageHeader,
  LoadingContainer,
  TabsComponentBootstrap,
  EditItem,
} from '../../components/Layout';
import { IconButton } from '../../components/Button/Button';
import {
  FaEdit,
  FaHeartBroken,
  FaHeart,
  FaCross,
  FaExternalLinkAlt,
  FaCopy,
} from 'react-icons/fa';
import UserBillingTab from './UserBillingTab';
import { UserDeliveryTokens } from './UserDeliveryTokens';
import { UserOrdersTab } from './UserOrdersTab';
import { getActiveTabFromNav } from '@/utils/url';
import { EmailValidationTab } from './EmailValidationTab';
import { LinkedOfflineUsersTab } from './LinkedOfflineUsersTab';
import { AchievementsTab } from './AchievementsTab';
import UserDetailTab from './UserDetailTab';
import { ProblemsTab } from './ProblemsTab';
import { ContentSection } from '../../components/Layout/ContentSection';
import UserContainersDisplay from './UserContainersDisplay';

// Components
import { SetCheckoutLimitModal } from './modals/SetCheckoutLimitModal';
import { DeleteUserModal } from './modals/DeleteUserModal';
import {
  PhoneNumberChangeModal,
  PhoneNumberConfirmationModal,
} from './modals/PhoneNumberChangeModals';
import { UserTransactionTabs } from './components/UserTransactionTabs';
import { PasswordChangeTab } from './components/PasswordChangeTab';

// Hooks and Types
import { useUserData } from './hooks/useUserData';
import { TransactionType } from './types';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

interface UserPageProps {
  userId: string;
}

const UserPage: React.FC<UserPageProps> = ({ userId }) => {
  const activeTab = getActiveTabFromNav();

  // Custom hook for user data management
  const {
    user,
    locations,
    oneTimeToken,
    linkedStore,
    firstCheckoutStore,
    userClaim,
    linkedStoreContainerStations,
    transactionHistory,
    transactionHistoryLoaded,
    transactionHistoryLoading,
    hasErrorLoading,
    reloadUser,
    loadTransactionHistory,
    copyJwtToClipboard,
    setUserDisabled,
    toggleIsInternal,
    toggleIsChargeableForOverdue,
    deleteUser,
    setCheckoutLimit,
    setNewPassword,
    setNewPhoneNumber,
  } = useUserData(userId);

  useDocumentTitle('PageTitles:user', user?.email || user?.userName);

  // Modal states
  const [showSetCheckoutLimitModal, setShowSetCheckoutLimitModal] =
    useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [isPhoneNumberChangeModalOpen, setIsPhoneNumberChangeModalOpen] =
    useState(false);
  const [
    isPhoneNumberChangeConfirmationModalOpen,
    setIsPhoneNumberChangeConfirmationModalOpen,
  ] = useState(false);
  const [pendingPhoneNumber, setPendingPhoneNumber] = useState('');
  const [changePhoneNumberError, setChangePhoneNumberError] = useState('');

  // Load transaction history when needed
  useEffect(() => {
    // Only load transaction history if the active tab is checkins or checkouts
    if (Number(activeTab) === 4 || Number(activeTab) === 5) {
      if (userId && !transactionHistoryLoaded && !transactionHistoryLoading) {
        loadTransactionHistory(userId);
      }
    }
  }, [
    userId,
    activeTab,
    transactionHistoryLoaded,
    transactionHistoryLoading,
    loadTransactionHistory,
  ]);

  // Handle phone number change
  const handlePhoneNumberSubmit = (phoneNumber: string) => {
    setPendingPhoneNumber(phoneNumber);
    setIsPhoneNumberChangeModalOpen(false);
    setIsPhoneNumberChangeConfirmationModalOpen(true);
    setChangePhoneNumberError('');
  };

  const handlePhoneNumberConfirm = async () => {
    try {
      await setNewPhoneNumber(user?.phoneNumber || '', pendingPhoneNumber);
      setIsPhoneNumberChangeConfirmationModalOpen(false);
      setIsPhoneNumberChangeModalOpen(false);
      setPendingPhoneNumber('');
    } catch (error: any) {
      setChangePhoneNumberError(error.message);
    }
  };

  // Handle checkout limit
  const handleSetCheckoutLimit = (
    isUnlimited: boolean,
    isDefault: boolean,
    limit: number,
  ) => {
    setCheckoutLimit(isUnlimited, isDefault, limit);
    setShowSetCheckoutLimitModal(false);
  };

  // Handle delete user
  const handleDeleteUser = (sendDeletionEvent: boolean) => {
    deleteUser(sendDeletionEvent);
    setShowDeleteUserModal(false);
  };

  // Payment info content
  const paymentInfoContent = () => {
    return user && user.pickpackId ? (
      <PaymentDetails pickpackId={user.pickpackId} />
    ) : (
      'no pickpack user Id'
    );
  };

  // Define tabs
  const tabs = [
    {
      title: i18n.t('Users:containers'),
      content: (
        <EditItem title={i18n.t('Users:containers')}>
          <UserContainersDisplay userId={userId} locations={locations} />
        </EditItem>
      ),
    },
    {
      title: i18n.t('Users:generalInfo'),
      content: (
        <EditItem title={i18n.t('Users:generalInfo')}>
          <UserDetailTab
            linkedStore={linkedStore}
            firstCheckoutStore={firstCheckoutStore}
            oneTimeToken={oneTimeToken}
            toggleIsChargeableForOverdue={toggleIsChargeableForOverdue}
            toggleIsInternal={toggleIsInternal}
            toggleShowSetCheckoutLimitModal={() =>
              setShowSetCheckoutLimitModal(true)
            }
            user={user}
            userClaim={userClaim}
            linkedStoreContainerStations={linkedStoreContainerStations}
            reloadUser={() => reloadUser(userId)}
          />
        </EditItem>
      ),
    },
    {
      title: i18n.t('Users:paymentInfo'),
      content: (
        <EditItem title={i18n.t('Users:paymentInfo')}>
          {paymentInfoContent()}
        </EditItem>
      ),
    },
    {
      title: i18n.t('Users:bills'),
      content: (
        <EditItem title={i18n.t('Users:bills')}>
          <UserBillingTab userId={user?.id} />
        </EditItem>
      ),
    },
    {
      title: i18n.t('Users:checkins'),
      content: (
        <EditItem title={i18n.t('Users:checkins')}>
          <UserTransactionTabs
            transactionHistory={transactionHistory}
            transactionHistoryLoaded={transactionHistoryLoaded}
            transactionHistoryLoading={transactionHistoryLoading}
            transactionType={TransactionType.checkin}
          />
        </EditItem>
      ),
    },
    {
      title: i18n.t('Users:checkouts'),
      content: (
        <EditItem title={i18n.t('Users:checkouts')}>
          <UserTransactionTabs
            transactionHistory={transactionHistory}
            transactionHistoryLoaded={transactionHistoryLoaded}
            transactionHistoryLoading={transactionHistoryLoading}
            transactionType={TransactionType.checkout}
          />
        </EditItem>
      ),
    },
    {
      title: i18n.t('Users:password'),
      content: (
        <EditItem title={i18n.t('Users:changePassword')}>
          <PasswordChangeTab onPasswordChange={setNewPassword} />
        </EditItem>
      ),
    },
    {
      title: i18n.t('Users:deliveryToken'),
      content: (
        <EditItem title={i18n.t('Users:deliveryTokens')}>
          <UserDeliveryTokens externalPickpackId={user?.pickpackId} />
        </EditItem>
      ),
    },
    {
      title: i18n.t('Users:pastOrders'),
      content: <UserOrdersTab externalPickpackId={user?.pickpackId || null} />,
    },
    {
      title: i18n.t('Users:emailVerification'),
      content: <EmailValidationTab pickpackId={user?.pickpackId || null} />,
    },
    {
      title: i18n.t('Users:linkedOfflineUsers'),
      content: <LinkedOfflineUsersTab vytalId={userId} />,
    },
    {
      title: i18n.t('Users:achievements'),
      content: <AchievementsTab pickpackId={user?.pickpackId || null} />,
    },
    {
      title: i18n.t('Users:problems'),
      content: <ProblemsTab uuid={user?.id || null} />,
    },
  ];

  return (
    <PageContainer>
      <ContentSection title={i18n.t('Users:user')} collapsable={false}>
        {/* Modals */}
        <SetCheckoutLimitModal
          isOpen={showSetCheckoutLimitModal}
          onClose={() => setShowSetCheckoutLimitModal(false)}
          onSubmit={handleSetCheckoutLimit}
        />

        <DeleteUserModal
          isOpen={showDeleteUserModal}
          onClose={() => setShowDeleteUserModal(false)}
          onConfirm={handleDeleteUser}
        />

        {user && (
          <>
            <PhoneNumberChangeModal
              isOpen={isPhoneNumberChangeModalOpen}
              userName={user.userName}
              onClose={() => {
                setIsPhoneNumberChangeModalOpen(false);
                setPendingPhoneNumber('');
              }}
              onSubmit={handlePhoneNumberSubmit}
            />

            <PhoneNumberConfirmationModal
              isOpen={isPhoneNumberChangeConfirmationModalOpen}
              userName={user.userName}
              oldPhoneNumber={user.phoneNumber}
              newPhoneNumber={pendingPhoneNumber}
              error={changePhoneNumberError}
              onClose={() => setIsPhoneNumberChangeConfirmationModalOpen(false)}
              onConfirm={handlePhoneNumberConfirm}
            />
          </>
        )}

        <PageHeader
          title={i18n.t('Users:user')}
          subtitle={`Id: ${userId}`}
          qrCode={
            <img
              alt="User QR code"
              src={'/Users/QrCodeForUser/' + userId + '/false'}
            />
          }
        >
          {user && (
            <>
              <IconButton
                className="mb-1 me-1"
                onClick={() => setIsPhoneNumberChangeModalOpen(true)}
                icon={<FaEdit />}
                label={i18n.t('Users:changePhoneNumber')}
              />
              <IconButton
                className="mb-1 me-1"
                onClick={copyJwtToClipboard}
                icon={<FaCopy />}
                label={i18n.t('Users:copyJwtToClipboard')}
              />
              {user.lockoutEnd ? (
                <IconButton
                  className="mb-1 me-1"
                  onClick={() => setUserDisabled(false)}
                  icon={<FaHeart />}
                  label={i18n.t('Users:activateUser')}
                />
              ) : (
                <IconButton
                  className="mb-1 me-1"
                  onClick={() => setUserDisabled(true)}
                  icon={<FaHeartBroken />}
                  label={i18n.t('Users:deactivateUser')}
                />
              )}
              {user.pickpackId != null && (
                <IconButton
                  className="mb-1 me-1"
                  icon={<FaExternalLinkAlt />}
                  label={i18n.t('Users:customerIoProfile')}
                  tag="a"
                  href={`https://fly.customer.io/env/93842/people/${user.pickpackId}`}
                  target="_blank"
                />
              )}
              <IconButton
                className="mb-1 me-1"
                label={'Show User in Container View'}
                icon={'🥣'}
                tag="a"
                href={`/containers/?searchStoreId=false&merchantId=&storeId=&isCurrentLocation=true&name=&searchName=false&typeId=1&searchTypeId=false&ownerId=${userId}&searchOwnerId=true&previousOwnerId=&searchPreviousOwnerId=false`}
              />
              <IconButton
                className="btn-danger mb-1 me-1"
                onClick={() => setShowDeleteUserModal(true)}
                icon={<FaCross />}
                label={i18n.t('Users:deleteUser')}
              />
            </>
          )}
        </PageHeader>

        {user ? (
          <TabsComponentBootstrap tabs={tabs} activeTab={activeTab} />
        ) : (
          <LoadingContainer
            hasErrorLoading={hasErrorLoading}
            errorMessage={i18n.t('Users:errorLoadingUser')}
          />
        )}
      </ContentSection>
    </PageContainer>
  );
};

// HOC wrapper to maintain compatibility with existing router setup
export const UserPageWithParams: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  return <UserPage userId={userId || ''} />;
};

// Keep the old export for backward compatibility
export { UserPageWithParams as UserPageWithHistory };
export default UserPage;
